import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/authors')
const GET_AUTHORS_ENDPOINT = `${API_BASE_URL}/all`
const ADD_AUTHOR_ENDPOINT = `${API_BASE_URL}/add`
const UPDATE_AUTHOR_ENDPOINT = `${API_BASE_URL}/update`
const DELETE_AUTHOR_ENDPOINT = `${API_BASE_URL}/delete`

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Author request failed (${response.status}) at ${url}`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findAuthors(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of ['authors', 'authorList', 'results', 'items', 'data']) {
    const authors = findAuthors(data[key])
    if (authors.length || Array.isArray(data[key])) return authors
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.authors) ??
    findNextCursor(data.authorList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

const addParam = (params, key, value) => {
  if (value === undefined || value === null || value === '') return
  params.set(key, String(value))
}

export async function getAuthors({
  cursor = null,
  page = 1,
  search = '',
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  addParam(params, 'search', search)

  const data = await request(`${GET_AUTHORS_ENDPOINT}?${params}`)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const authors = findAuthors(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor,
  )

  return {
    authors,
    nextCursor,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    currentPage,
  }
}

export async function createAuthor(author) {
  const data = await request(ADD_AUTHOR_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(author),
  })
  return data?.data || data
}

export async function updateAuthor(authorId, author) {
  const data = await request(
    `${UPDATE_AUTHOR_ENDPOINT}/${encodeURIComponent(authorId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(author),
    },
  )
  return data?.data || data
}

export async function deleteAuthor(authorId) {
  await request(`${DELETE_AUTHOR_ENDPOINT}/${encodeURIComponent(authorId)}`, {
    method: 'DELETE',
  })
}
